import React, { useState } from 'react';
import useImageCrop from '../hooks/useImageCrop';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, PenLine, Trash2, MapPin, X, Camera, Loader2, Lock } from 'lucide-react';
import { useData, LOCATION_HARD_CAP } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import { waSupport } from '../utils/support';

const Locations = () => {
  const { locations, refresh } = useData();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '', cnpj: '', logo_url: '' });

  // Locais de atendimento são ilimitados em todo plano — o teto abaixo é só
  // uma trava técnica contra abuso, igual pra todo mundo, não um benefício
  // de plano (por isso não vem de PLAN_LIMITS).
  const atLimit = locations.length >= LOCATION_HARD_CAP;

  const handleOpen = (loc = null) => {
    if (!loc && atLimit) return;
    if (loc) {
      setFormData({ name: loc.name, address: loc.address, phone: loc.phone, cnpj: loc.cnpj, logo_url: loc.logo_url });
      setEditingId(loc.id);
    } else {
      setFormData({ name: '', address: '', phone: '', cnpj: '', logo_url: '' });
      setEditingId(null);
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name) return alert('Nome é obrigatório');
    if (!editingId && atLimit) return alert(`Limite técnico de ${LOCATION_HARD_CAP} locais atingido. Fale com o suporte.`);
    setLoading(true);
    try {
      if (editingId) {
        const { error } = await supabase.from('locations').update(formData).eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('locations').insert([{ ...formData, user_id: user.id }]);
        if (error) throw error;
      }
      refresh();
      setIsModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Excluir este local?')) return;
    await supabase.from('locations').delete().eq('id', id);
    refresh();
  };

  const { cropImages, cropModal } = useImageCrop();

  const handleLogoUpload = async (e) => {
    const picked = e.target.files[0];
    e.target.value = '';
    if (!picked) return;
    const cropped = await cropImages([picked]);
    if (!cropped) return;
    const [file] = cropped;
    if (!file) return;
    setLoading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/loc-${crypto.randomUUID()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('report-images').upload(fileName, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('report-images').getPublicUrl(fileName);
      setFormData(prev => ({ ...prev, logo_url: publicUrl }));
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {cropModal}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight mb-2">Locais de atendimento</h1>
          <p className="text-sm text-muted tabular-nums">{locations.length} locais</p>
        </div>
        <button onClick={() => handleOpen()} disabled={atLimit} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
          Novo local
        </button>
      </div>

      {atLimit && (
        <div className="flex items-center gap-2 mb-8 text-xs text-muted bg-paper-alt border border-line rounded-card px-4 py-3">
          Você atingiu o limite técnico de {LOCATION_HARD_CAP} locais.
          <a href={waSupport()} target="_blank" rel="noopener noreferrer" className="text-navy font-semibold hover:underline">Falar com o suporte</a>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {locations.map((loc) => (
          <div key={loc.id} className="bg-paper p-8 rounded-card border border-line shadow-card hover:border-navy/40 transition-colors group relative overflow-hidden">
            <div className="flex justify-between items-start mb-6">
              <div className="w-16 h-16 rounded-card bg-paper-alt border border-line overflow-hidden flex items-center justify-center">
                {loc.logo_url ? <img src={loc.logo_url} alt={`Logo de ${loc.name}`} className="w-full h-full object-cover" /> : <MapPin className="text-muted w-7 h-7" aria-hidden="true" />}
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleOpen(loc)} title="Editar local" aria-label={`Editar ${loc.name}`} className="w-11 h-11 bg-paper-alt text-muted rounded-card flex items-center justify-center hover:bg-navy hover:text-white transition-colors"><PenLine className="w-4 h-4" aria-hidden="true" /></button>
                <button onClick={() => handleDelete(loc.id)} title="Excluir local" aria-label={`Excluir ${loc.name}`} className="w-11 h-11 bg-danger-tint text-danger rounded-card flex items-center justify-center hover:bg-danger hover:text-white transition-colors"><Trash2 className="w-4 h-4" aria-hidden="true" /></button>
              </div>
            </div>
            <h3 className="font-display text-xl font-bold text-ink mb-2">{loc.name}</h3>
            <p className="text-xs text-muted leading-relaxed line-clamp-2">{loc.address || 'Endereço não cadastrado'}</p>
          </div>
        ))}
        {locations.length === 0 && (
          /* Estado vazio desenhado */
          <div className="col-span-full py-20 text-center bg-paper rounded-card border-2 border-dashed border-line">
            <h3 className="font-display text-xl font-bold text-ink mb-2">Nenhum local cadastrado</h3>
            <p className="text-muted max-w-xs mx-auto mb-6">Cadastre sua clínica, hospital ou consultório para que o cabeçalho do PDF saia com os dados corretos.</p>
            <button onClick={() => handleOpen()} className="btn-primary">
              Cadastrar meu primeiro local
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 bg-navy/50 backdrop-blur-[2px] z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="bg-paper rounded-card p-8 w-full max-w-2xl shadow-xl overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-center mb-8">
                <h2 className="font-display text-2xl font-bold text-navy tracking-tight">{editingId ? 'Editar local' : 'Novo local'}</h2>
                <button onClick={() => setIsModalOpen(false)} className="p-2.5 min-h-[44px] min-w-[44px] text-muted hover:text-ink transition-colors" aria-label="Fechar"><X className="w-6 h-6" aria-hidden="true" /></button>
              </div>
              <form onSubmit={handleSave} className="space-y-8">
                <div className="flex items-center gap-6">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-card bg-paper-alt border-2 border-dashed border-line flex items-center justify-center overflow-hidden cursor-pointer hover:border-navy transition-colors">
                      {formData.logo_url ? <img src={formData.logo_url} alt="Prévia do logo" className="w-full h-full object-cover" /> : <Camera className="text-muted w-8 h-8" aria-hidden="true" />}
                      <input type="file" onChange={handleLogoUpload} className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" aria-label="Enviar logo" />
                    </div>
                    <p className="text-[10px] font-semibold text-muted uppercase tracking-widest text-center mt-2">Logo (Opcional)</p>
                  </div>
                  <div className="flex-1">
                    <label htmlFor="loc-name" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Nome da clínica ou hospital</label>
                    <input id="loc-name" type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="input-field" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label htmlFor="loc-address" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Endereço completo</label>
                    <input id="loc-address" type="text" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="input-field" />
                  </div>
                  <div>
                    <label htmlFor="loc-phone" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">Telefone</label>
                    <input id="loc-phone" type="text" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="input-field font-mono" />
                  </div>
                  <div>
                    <label htmlFor="loc-cnpj" className="block text-xs font-semibold text-muted uppercase tracking-widest mb-2">CNPJ</label>
                    <input id="loc-cnpj" type="text" value={formData.cnpj} onChange={e => setFormData({...formData, cnpj: e.target.value})} className="input-field font-mono" />
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full uppercase tracking-widest text-xs">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : 'Salvar local'}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Locations;
